import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../store/store';
import { DrawerPager } from './DrawerPager';

type ResultPagerProps = {
  ownerName: string;
  mapShortId: string;
};

/**
 * Pager component for navigating between sibling results with the same parent objective.
 * - Reads results ordering from the objective in Redux
 * - Resolves current position via ?resultId= from search params
 * - Preserves all other search params on navigation (functional setSearchParams merge)
 */
export function ResultPager({ ownerName: _ownerName, mapShortId: _mapShortId }: ResultPagerProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const resultShortId = searchParams.get('resultId') ?? undefined;

  // Get the parent objective to read its results array
  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((o) => o.shortId === objectiveShortId),
  );

  const results = objective?.results ?? [];

  // Find the current index based on resultShortId
  const currentIndex = results.findIndex((r) => r.shortId === resultShortId);

  const navigateToResult = (targetShortId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('resultId', targetShortId);
      // Ensure we're in result context
      next.set('tab', 'result');
      // ?from / ?fromInterval are intentionally preserved here (mr-3j4t.1):
      // pager navigation stays within the Result context, so the back button
      // must continue pointing to the original originating tab.
      return next;
    });
  };

  const handlePrev = () => {
    if (currentIndex <= 0) return;
    navigateToResult(results[currentIndex - 1].shortId);
  };

  const handleNext = () => {
    if (currentIndex < 0 || currentIndex >= results.length - 1) return;
    navigateToResult(results[currentIndex + 1].shortId);
  };

  return (
    <DrawerPager
      currentIndex={currentIndex}
      total={results.length}
      onPrev={handlePrev}
      onNext={handleNext}
      label="Result navigation"
    />
  );
}
